import React, {ChangeEvent, useCallback} from "react";
import {styled, theme} from "@reside/ui";

import {RoundedSearchBox} from "../search-box";
import {
  MAX_SEARCH_TERM_LENGTH,
  MIN_SEARCH_TERM_LENGTH,
} from "../dashboard-table/hooks/useWorkflowsQuery";

type Props = Readonly<{
  value: string;
  onChange: (value: string) => void;
}>;

/**
 * Filters the board in place, unlike `AdminAdmissionsSearch`, which is a
 * typeahead that navigates away on select. `RoundedSearchBox` expects a
 * downshift prop getter; `props => props` reuses its styling without the
 * typeahead behaviour.
 */
export const DashboardSearchInput = ({value, onChange}: Props) => {
  const onInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value),
    [onChange],
  );

  const onClearClick = useCallback(() => onChange(""), [onChange]);

  const showHint =
    value.trim().length > 0 && value.trim().length < MIN_SEARCH_TERM_LENGTH;

  return (
    <Wrapper>
      <RoundedSearchBox
        ariaLabel="Search workflows by resident name or admission ID"
        maxLength={MAX_SEARCH_TERM_LENGTH}
        placeholder="Search by name or ID"
        testId="dashboard-search"
        value={value}
        onInputChange={onInputChange}
        onClearClick={onClearClick}
        showClearButton={value.length > 0}
        downshift={props => props}
      />
      {showHint && (
        <Hint data-test-id="dashboard-search-hint">
          Keep typing, {MIN_SEARCH_TERM_LENGTH} characters minimum
        </Hint>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
`;

const Hint = styled.div`
  position: absolute;
  top: 100%;
  left: 12px;
  margin-top: 4px;
  font-size: 11px;
  color: ${theme.color.gray100};
  white-space: nowrap;
`;
